import { useState } from 'react'

import { Box, Button, Typography } from '@material-ui/core'
import { ListItem, ListItemIcon, ListItemText } from '@material-ui/core'
import { Theme, makeStyles } from '@material-ui/core/styles'
import Avatar, { ICON_COLOR } from '@scoir/avatar'
import { imageAsFullImageSrc } from '@scoir/avatar-image-url'
import { ErrorBoundary } from '@scoir/error-reporter'
import { useDispatch } from 'react-redux'

import api from 'common/api'
import { actions as inviterActions } from 'store/inviters'
import { Invite } from 'store/inviters/invitations-service'
import { actions as snactions } from 'store/snackbar'
import { actions as userActions } from 'store/user'

import DismissInviteModal from '../DismissInviteModal'

const useStyles = makeStyles((theme: Theme) => ({
  pending: {
    background: 'rgba(0, 125, 170, 0.08)',
  },
  dismissButton: {
    marginLeft: theme.spacing(0),
    marginTop: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: theme.spacing(1),
      marginTop: theme.spacing(0),
    },
  },
  subHeader: {
    overflowWrap: 'break-word',
  },
}))

interface PendingInviteProps {
  invite: Invite
}

const PendingInvite = ({ invite }: PendingInviteProps) => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const [dismissableInviteId, setDismissableInviteId] = useState<string>()

  const { inviterId, inviterFirstName, inviterLastName, inviterEmail, inviterAvatarUrl } = invite
  const avatarLabel = `${inviterFirstName} Avatar`

  const acceptInvite = () => {
    api
      .post<{ inviterId: string }, any>(`/api/student/relate/accept`, { inviterId })
      .then(() => {
        dispatch(userActions.fetchUser())
        dispatch(snactions.addSnackbarItem(`Success! You are now linked with ${inviterFirstName} ${inviterLastName}.`))
      })
      .catch((err) => {
        let errorMessage = `Failed to accept invite, please try again later.`
        if (err.status === 404) {
          errorMessage = `Failed to accept invite, invite no longer exists.`
        }
        dispatch(snactions.addSnackbarItem(errorMessage))
      })
      .finally(() => {
        dispatch(inviterActions.fetchInviters())
      })
  }

  return (
    <>
      <ListItem className={classes.pending}>
        <ListItemIcon>
          <Avatar
            size="sm"
            sourceUrl={inviterAvatarUrl && imageAsFullImageSrc(inviterAvatarUrl)}
            fallbackIcon={inviterFirstName.charAt(0)}
            variant="circular"
            iconColor={ICON_COLOR.info}
            aria-label={avatarLabel}
          />
        </ListItemIcon>
        <ListItemText>
          <Typography variant="body1">
            {inviterFirstName} {inviterLastName}
          </Typography>
          <Box mb={1}>
            <Typography
              variant="body2"
              color="textSecondary"
              className={classes.subHeader}
            >
              {inviterEmail} invited you to link with them
            </Typography>
          </Box>
          <ErrorBoundary>
            <Box
              display="flex"
              flexWrap="wrap"
            >
              <Button
                variant="contained"
                size="large"
                color="primary"
                onClick={() => acceptInvite()}
              >
                Accept
              </Button>
              <Button
                variant="text"
                size="large"
                color="primary"
                className={classes.dismissButton}
                onClick={() => setDismissableInviteId(inviterId)}
              >
                Reject
              </Button>
            </Box>
          </ErrorBoundary>
        </ListItemText>
      </ListItem>
      <ErrorBoundary>
        <DismissInviteModal
          show={!!dismissableInviteId}
          inviterId={dismissableInviteId ?? ''}
          onDismiss={() => {
            dispatch(inviterActions.fetchInviters())
          }}
          onClose={() => setDismissableInviteId(undefined)}
        />
      </ErrorBoundary>
    </>
  )
}

export default PendingInvite
